import { areWidgetFrameAppearancesEqual, cloneWidgetFrameAppearance, isWidgetFrameAppearance, } from "./widget-frame-appearance.js";
function areWidgetLayoutTransitionPoliciesEqual(a, b) {
    if (a === undefined || b === undefined) {
        return a === b;
    }
    if (a.kind !== b.kind || a.durationMs !== b.durationMs) {
        return false;
    }
    if (a.kind === "instant") {
        return true;
    }
    return (b.kind === "timed" &&
        a.easing.every((point, index) => point === b.easing[index]));
}
export function areViewHostSnapshotsEqual(a, b) {
    if (a === undefined || b === undefined) {
        return a === b;
    }
    if (a.kind !== b.kind ||
        a.isOpen !== b.isOpen ||
        a.isLoading !== b.isLoading ||
        a.width !== b.width ||
        a.height !== b.height ||
        !areWidgetFrameAppearancesEqual(a.frameAppearance, b.frameAppearance) ||
        a.flowHandleId !== b.flowHandleId ||
        !areWidgetLayoutTransitionPoliciesEqual(a.transitionPolicy, b.transitionPolicy) ||
        a.transitionSchedule?.startAtEpochMs !==
            b.transitionSchedule?.startAtEpochMs ||
        a.transitionSchedule?.sequence !== b.transitionSchedule?.sequence ||
        a.transitionSchedule?.transactionId !== b.transitionSchedule?.transactionId) {
        return false;
    }
    return (a.kind === "pendingFlowHandle" ||
        (b.kind === "flowRun" && a.flowRunId === b.flowRunId));
}
export function cloneViewHostSnapshots(snapshots) {
    return snapshots.map((snapshot) => {
        const frameAppearance = snapshot.frameAppearance === undefined
            ? {}
            : {
                frameAppearance: cloneWidgetFrameAppearance(snapshot.frameAppearance),
            };
        if (snapshot.transitionPolicy?.kind === "timed") {
            return {
                ...snapshot,
                ...frameAppearance,
                transitionPolicy: {
                    ...snapshot.transitionPolicy,
                    easing: [...snapshot.transitionPolicy.easing],
                },
                ...(snapshot.transitionSchedule
                    ? { transitionSchedule: { ...snapshot.transitionSchedule } }
                    : {}),
            };
        }
        return {
            ...snapshot,
            ...frameAppearance,
            ...(snapshot.transitionPolicy
                ? { transitionPolicy: { ...snapshot.transitionPolicy } }
                : {}),
            ...(snapshot.transitionSchedule
                ? { transitionSchedule: { ...snapshot.transitionSchedule } }
                : {}),
        };
    });
}
export function resolveInstanceFlowStateShouldRender(detail) {
    return detail.shouldRender ?? (detail.isOpen || detail.isLoading);
}
/** @internal Trusted host-realm navigation adapter event. */
export const NAVIGATION_OUTCOMES = [
    "succeeded",
    "failed",
    "unhandled",
];
export function isNavigationOutcome(value) {
    return NAVIGATION_OUTCOMES.some((outcome) => outcome === value);
}
export const HANDLE_INVALIDATED_REASON_CODES = [
    "RESET",
    "CLOSED",
    "STALE_HANDLE",
    "OWNERSHIP_CONFLICT",
    "OWNER_DISPOSED",
    "UPSTREAM_INVALIDATED",
    "INTERNAL",
];
export const HANDLE_INVALIDATED_SOURCES = [
    "loader",
    "core",
];
const isNonEmptyString = (v) => typeof v === "string" && v.trim().length > 0;
const isRecord = (v) => typeof v === "object" && v !== null;
const HANDLE_INVALIDATED_REASON_CODE_SET = new Set(HANDLE_INVALIDATED_REASON_CODES);
const isHandleInvalidatedReasonCode = (v) => typeof v === "string" && HANDLE_INVALIDATED_REASON_CODE_SET.has(v);
const HANDLE_INVALIDATED_SOURCE_SET = new Set(HANDLE_INVALIDATED_SOURCES);
const isHandleInvalidatedSource = (v) => typeof v === "string" && HANDLE_INVALIDATED_SOURCE_SET.has(v);
function isWidgetLayoutTransitionPolicyLike(value) {
    if (value === undefined) {
        return true;
    }
    if (!isRecord(value)) {
        return false;
    }
    if (value.kind === "instant") {
        return value.durationMs === 0;
    }
    if (value.kind !== "timed") {
        return false;
    }
    const easing = value.easing;
    return (typeof value.durationMs === "number" &&
        Number.isFinite(value.durationMs) &&
        value.durationMs > 0 &&
        Array.isArray(easing) &&
        easing.length === 4 &&
        typeof easing[0] === "number" &&
        easing[0] >= 0 &&
        easing[0] <= 1 &&
        typeof easing[1] === "number" &&
        Number.isFinite(easing[1]) &&
        typeof easing[2] === "number" &&
        easing[2] >= 0 &&
        easing[2] <= 1 &&
        typeof easing[3] === "number" &&
        Number.isFinite(easing[3]));
}
function isWidgetLayoutTransitionScheduleLike(value) {
    if (value === undefined) {
        return true;
    }
    return (isRecord(value) &&
        typeof value.sequence === "number" &&
        Number.isSafeInteger(value.sequence) &&
        value.sequence >= 0 &&
        isNonEmptyString(value.transactionId) &&
        typeof value.startAtEpochMs === "number" &&
        Number.isFinite(value.startAtEpochMs));
}
export function isFlowStateChangedDetail(detail) {
    if (!isRecord(detail))
        return false;
    const { instanceId, isOpen, isLoading, width, height, flowHandleId, transitionPolicy, } = detail;
    return (isNonEmptyString(instanceId) &&
        typeof isOpen === "boolean" &&
        typeof isLoading === "boolean" &&
        (typeof width === "undefined" || typeof width === "number") &&
        (typeof height === "undefined" || typeof height === "number") &&
        isWidgetLayoutTransitionPolicyLike(transitionPolicy) &&
        isNonEmptyString(flowHandleId));
}
export function isFlowLayoutUpdatedDetail(detail) {
    if (!isRecord(detail))
        return false;
    const { instanceId, flowHandleId, width, height, transitionPolicy } = detail;
    return (isNonEmptyString(instanceId) &&
        isNonEmptyString(flowHandleId) &&
        typeof width === "number" &&
        Number.isFinite(width) &&
        typeof height === "number" &&
        Number.isFinite(height) &&
        isWidgetLayoutTransitionPolicyLike(transitionPolicy) &&
        transitionPolicy?.kind === "timed");
}
export function isInstanceFlowStateChangedDetail(detail) {
    if (!isRecord(detail))
        return false;
    const { instanceId, isOpen, isLoading, shouldRender, width, height, flowHandleId, transitionPolicy, } = detail;
    return (isNonEmptyString(instanceId) &&
        typeof isOpen === "boolean" &&
        typeof isLoading === "boolean" &&
        (typeof shouldRender === "undefined" ||
            typeof shouldRender === "boolean") &&
        (typeof width === "undefined" || typeof width === "number") &&
        (typeof height === "undefined" || typeof height === "number") &&
        isWidgetLayoutTransitionPolicyLike(transitionPolicy) &&
        (typeof flowHandleId === "undefined" || isNonEmptyString(flowHandleId)));
}
function isFlowStateShape(detail) {
    const { isOpen, isLoading, width, height, transitionPolicy } = detail;
    return (typeof isOpen === "boolean" &&
        typeof isLoading === "boolean" &&
        (typeof width === "undefined" || typeof width === "number") &&
        (typeof height === "undefined" || typeof height === "number") &&
        isWidgetLayoutTransitionPolicyLike(transitionPolicy));
}
export function isViewHostSnapshot(snapshot) {
    if (!isRecord(snapshot) || !isFlowStateShape(snapshot)) {
        return false;
    }
    if (snapshot.frameAppearance !== undefined &&
        !isWidgetFrameAppearance(snapshot.frameAppearance)) {
        return false;
    }
    if (!isWidgetLayoutTransitionScheduleLike(snapshot.transitionSchedule)) {
        return false;
    }
    if (snapshot.kind === "flowRun") {
        return (isNonEmptyString(snapshot.flowRunId) &&
            (typeof snapshot.flowHandleId === "undefined" ||
                isNonEmptyString(snapshot.flowHandleId)));
    }
    if (snapshot.kind === "pendingFlowHandle") {
        return isNonEmptyString(snapshot.flowHandleId);
    }
    return false;
}
export function isInstanceViewHostSnapshotsChangedDetail(detail) {
    if (!isRecord(detail))
        return false;
    const { instanceId, snapshots } = detail;
    return (isNonEmptyString(instanceId) &&
        Array.isArray(snapshots) &&
        Array.from(snapshots).every(isViewHostSnapshot));
}
export function isOpenRequestedDetail(detail) {
    if (!isRecord(detail))
        return false;
    const { instanceId, source, flowId, flowHandleId, hideCloseButton } = detail;
    return (isNonEmptyString(instanceId) &&
        (source === "command" || source === "targeting") &&
        isNonEmptyString(flowId) &&
        (typeof flowHandleId === "undefined" || isNonEmptyString(flowHandleId)) &&
        (typeof hideCloseButton === "undefined" ||
            typeof hideCloseButton === "boolean"));
}
export function isNavigationRequestedDetail(detail) {
    if (!isRecord(detail))
        return false;
    const { instanceId, url, target, version, settle } = detail;
    try {
        const protocol = typeof url === "string" ? new URL(url).protocol : "";
        return (isNonEmptyString(instanceId) &&
            (protocol === "http:" || protocol === "https:") &&
            typeof version === "number" &&
            Number.isSafeInteger(version) &&
            version > 0 &&
            (settle === undefined || typeof settle === "function") &&
            ((version === 1 && target === "_blank") ||
                (version > 1 &&
                    (target === undefined || target === "self" || target === "blank"))));
    }
    catch {
        return false;
    }
}
export function isHostActionRequestedDetail(detail) {
    if (!isRecord(detail))
        return false;
    const { instanceId, definition, settle } = detail;
    return (isNonEmptyString(instanceId) &&
        typeof settle === "function" &&
        isRecord(definition) &&
        isNonEmptyString(definition.key) &&
        typeof definition.version === "number" &&
        Number.isSafeInteger(definition.version) &&
        definition.version > 0);
}
export function isHandleInvalidatedDetail(detail) {
    if (!isRecord(detail))
        return false;
    const { instanceId, handleKind, handleId, reasonCode, reasonMessage, relatedRequestId, source, at, } = detail;
    return (isNonEmptyString(instanceId) &&
        isNonEmptyString(handleKind) &&
        isNonEmptyString(handleId) &&
        isHandleInvalidatedReasonCode(reasonCode) &&
        (typeof reasonMessage === "undefined" ||
            typeof reasonMessage === "string") &&
        (typeof relatedRequestId === "undefined" ||
            isNonEmptyString(relatedRequestId)) &&
        isHandleInvalidatedSource(source) &&
        typeof at === "number" &&
        Number.isFinite(at));
}
export function isCommandSettledDetail(detail) {
    if (!isRecord(detail))
        return false;
    const { requestId, instanceId, kind, ok, error } = detail;
    if (!isNonEmptyString(requestId) ||
        (typeof instanceId !== "string" && instanceId !== null) ||
        typeof kind !== "string" ||
        typeof ok !== "boolean") {
        return false;
    }
    if (ok === true) {
        return true;
    }
    if (ok === false && isRecord(error)) {
        return typeof error.message === "string";
    }
    return false;
}
/**
 * Return the host-facing handle result for a command settlement, or null.
 * Use in the loader when projecting instance:command:settled; keeps the host contract in one place (no zod).
 */
export function getHostHandleResultFromSettlement(detail) {
    return getFlowHandleFromSettlementDetail(detail);
}
const toCanonicalUtcTimestamp = (value) => {
    if (typeof value !== "string") {
        return null;
    }
    const normalizedValue = value.trim();
    if (!/^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}\.[0-9]{3}Z$/u.test(normalizedValue)) {
        return null;
    }
    const date = new Date(normalizedValue);
    return !Number.isNaN(date.getTime()) && date.toISOString() === normalizedValue
        ? normalizedValue
        : null;
};
/** Return a validated feedback receipt from a successful capture settlement. */
export function getFeedbackSubmissionFromSettlementDetail(detail) {
    if (!detail.ok ||
        detail.kind !== "instanceFeedback.capture" ||
        !isRecord(detail.result)) {
        return null;
    }
    const { evaluationId, status } = detail.result;
    const capturedAt = toCanonicalUtcTimestamp(detail.result.capturedAt);
    if (status !== "accepted" ||
        !isNonEmptyString(evaluationId) ||
        capturedAt === null) {
        return null;
    }
    return {
        capturedAt,
        evaluationId: evaluationId.trim(),
        status,
    };
}
const toFeedbackMineEvaluation = (value) => {
    if (!isRecord(value)) {
        return null;
    }
    const { category, comment, evaluationId, reasons } = value;
    const capturedAt = toCanonicalUtcTimestamp(value.capturedAt);
    const evaluationValue = value.value;
    if (capturedAt === null ||
        (typeof category !== "string" && category !== null) ||
        (typeof comment !== "string" && comment !== null) ||
        !isNonEmptyString(evaluationId) ||
        !Array.isArray(reasons) ||
        !reasons.every((reason) => typeof reason === "string") ||
        (typeof evaluationValue !== "boolean" &&
            typeof evaluationValue !== "number" &&
            typeof evaluationValue !== "string" &&
            evaluationValue !== null) ||
        (typeof evaluationValue === "number" && !Number.isFinite(evaluationValue))) {
        return null;
    }
    return {
        capturedAt,
        category,
        comment,
        evaluationId: evaluationId.trim(),
        reasons,
        value: evaluationValue,
    };
};
/** Return a validated authenticated-feedback read result from a settlement. */
export function getFeedbackReadFromSettlementDetail(detail) {
    if (!detail.ok ||
        detail.kind !== "instanceFeedback.listMine" ||
        !isRecord(detail.result) ||
        !Array.isArray(detail.result.items)) {
        return null;
    }
    const items = [];
    for (const item of detail.result.items) {
        if (!isRecord(item) || !("evaluation" in item)) {
            return null;
        }
        if (item.evaluation === null) {
            items.push({ evaluation: null });
            continue;
        }
        const evaluation = toFeedbackMineEvaluation(item.evaluation);
        if (!evaluation) {
            return null;
        }
        items.push({ evaluation });
    }
    return { items };
}
const HANDLE_RETURNING_KINDS = [
    "open",
    "prerender",
    "prefetch",
];
const HANDLE_RETURNING_KIND_SET = new Set(HANDLE_RETURNING_KINDS);
const isHandleReturningKind = (kind) => HANDLE_RETURNING_KIND_SET.has(kind);
/**
 * Extract flow handle from a successful command settlement when the command
 * is one that can return a handle (open, prerender, prefetch). Uses plain
 * checks only (no Zod). Returns null if detail is not ok, kind is not one of
 * those, or result does not contain a non-empty flowHandleId.
 */
export function getFlowHandleFromSettlementDetail(detail) {
    if (!detail.ok || !isHandleReturningKind(detail.kind)) {
        return null;
    }
    const result = detail.result;
    if (!isRecord(result)) {
        return null;
    }
    const { flowHandleId, flowRunId } = result;
    if (!isNonEmptyString(flowHandleId)) {
        return null;
    }
    return {
        flowHandleId,
        ...(isNonEmptyString(flowRunId) && { flowRunId }),
    };
}
const nonEmptyOrNull = (value) => typeof value === "string" && value.length > 0 ? value : null;
/**
 * Parse a command settlement into a single allocation object when the command
 * returned a flow handle (open/prerender/prefetch). Returns null otherwise.
 */
export function getFlowHandleAllocationFromSettlement(detail) {
    const handle = getFlowHandleFromSettlementDetail(detail);
    if (!handle) {
        return null;
    }
    return {
        flowHandleId: handle.flowHandleId,
        flowRunId: handle.flowRunId ?? null,
        instanceId: nonEmptyOrNull(detail.instanceId ?? null),
    };
}
